import React, { useState, useEffect, useRef } from "react";

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function isInRange(date, start, end) {
  if (!start || !end || !date) return false;
  const d = date.getTime();
  const s = Math.min(start.getTime(), end.getTime());
  const e = Math.max(start.getTime(), end.getTime());
  return d > s && d < e;
}

function CalendarMonth({ year, month, startDate, endDate, hoverDate, onDayClick, onDayHover, onPrev, onNext, showPrev, showNext }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const rangeEnd = endDate || hoverDate;

  return (
    <div className="date-picker-calendar">
      <div className="calendar-header">
        <button className="calendar-nav" onClick={onPrev} style={{ visibility: showPrev ? "visible" : "hidden" }}>←</button>
        <div className="calendar-title">{MONTHS[month]} {year}</div>
        <button className="calendar-nav" onClick={onNext} style={{ visibility: showNext ? "visible" : "hidden" }}>→</button>
      </div>

      <div className="calendar-grid">
        {DAYS.map(d => (
          <div key={d} className="calendar-day-label">{d}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={`empty-${i}`} className="calendar-day empty" />;

          const isStart = isSameDay(date, startDate);
          const isEnd = rangeEnd && isSameDay(date, rangeEnd);
          const inRange = startDate && isInRange(date, startDate, rangeEnd);
          const isToday = isSameDay(date, today);
          const isFuture = date > today;

          let className = "calendar-day";
          if (isStart) className += " range-start";
          if (isEnd && startDate) className += " range-end";
          if (inRange) className += " in-range";
          if (isToday) className += " today";
          if (isFuture) className += " disabled";

          return (
            <button
              key={i}
              className={className}
              onClick={() => !isFuture && onDayClick(date)}
              onMouseEnter={() => !isFuture && onDayHover(date)}
              disabled={isFuture}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function DateRangePicker({ onApply, onClose }) {
  const today = new Date();
  const [leftYear, setLeftYear]   = useState(today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear());
  const [leftMonth, setLeftMonth] = useState(today.getMonth() === 0 ? 11 : today.getMonth() - 1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate]     = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const ref = useRef();

  // Right calendar is always one month ahead of left
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;
  const rightYear  = leftMonth === 11 ? leftYear + 1 : leftYear;

  function handlePrev() {
    if (leftMonth === 0) { setLeftMonth(11); setLeftYear(y => y - 1); }
    else setLeftMonth(m => m - 1);
  }

  function handleNext() {
    // Don't go past current month on the right calendar
    const nextRight = rightMonth === 11 ? 0 : rightMonth + 1;
    const nextRightYear = rightMonth === 11 ? rightYear + 1 : rightYear;
    if (nextRightYear > today.getFullYear() || (nextRightYear === today.getFullYear() && nextRight > today.getMonth())) return;
    if (leftMonth === 11) { setLeftMonth(0); setLeftYear(y => y + 1); }
    else setLeftMonth(m => m + 1);
  }

  function handleDayClick(date) {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    }
  }

  function fmt(date) {
    if (!date) return "—";
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function handleApply() {
    if (!startDate || !endDate) return;
    const pad = n => String(n).padStart(2, "0");
    const fmtISO = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    onApply({ from: fmtISO(startDate), to: fmtISO(endDate), label: `${fmt(startDate)} – ${fmt(endDate)}` });
  }

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [onClose]);

  const canGoNext = !(rightYear === today.getFullYear() && rightMonth === today.getMonth());

  return (
    <div className="date-picker-dropdown" ref={ref}>
      <div className="date-picker-calendars">
        <CalendarMonth
          year={leftYear} month={leftMonth}
          startDate={startDate} endDate={endDate} hoverDate={hoverDate}
          onDayClick={handleDayClick} onDayHover={setHoverDate}
          onPrev={handlePrev} onNext={handleNext}
          showPrev={true} showNext={false}
        />
        <div style={{ width: "1px", background: "rgba(0,0,0,0.06)" }} />
        <CalendarMonth
          year={rightYear} month={rightMonth}
          startDate={startDate} endDate={endDate} hoverDate={hoverDate}
          onDayClick={handleDayClick} onDayHover={setHoverDate}
          onPrev={handlePrev} onNext={handleNext}
          showPrev={false} showNext={canGoNext}
        />
      </div>

      <div className="date-picker-footer">
        <div className="date-picker-selected-label">
          {startDate && !endDate && <>Select end date</>}
          {startDate && endDate && <><span>{fmt(startDate)}</span> → <span>{fmt(endDate)}</span></>}
          {!startDate && <>Select start date</>}
        </div>
        <div className="date-picker-actions">
          <button className="time-btn" onClick={() => { setStartDate(null); setEndDate(null); }}>
            Clear
          </button>
          <button
            className="time-btn"
            style={{ background: startDate && endDate ? "var(--green)" : "var(--green-pale)", color: startDate && endDate ? "white" : "var(--muted)", borderColor: "transparent" }}
            onClick={handleApply}
            disabled={!startDate || !endDate}
          >
            Apply →
          </button>
        </div>
      </div>
    </div>
  );
}